import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/api';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('th');
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Load translations on mount
  useEffect(() => {
    loadTranslations();
  }, []);

  const loadTranslations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/language/translations`);
      const data = await response.json();
      if (data.success) {
        setTranslations(data.translations);
      }
    } catch (error) {
      console.error('Failed to load translations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load branch language setting
  const loadBranchLanguage = useCallback(async (branchId) => {
    if (!branchId) return;
    try {
      const data = await fetchWithAuth(`/api/language/branch/${branchId}`);
      if (data.success && data.language) {
        setCurrentLanguage(data.language);
      }
    } catch (error) {
      console.error('Failed to load branch language:', error);
    }
  }, []);

  // Update branch language setting
  const updateBranchLanguage = async (branchId, language) => {
    try {
      await fetchWithAuth(`/api/language/branch/${branchId}`, {
        method: 'PUT',
        body: JSON.stringify({ language })
      });
      setCurrentLanguage(language);
    } catch (error) {
      console.error('Failed to update branch language:', error);
    }
  };

  // Get translation for a key
  const print_lang = useCallback((key, fallback = '') => {
    if (!translations[key]) {
      return fallback || key;
    }
    const translation = currentLanguage === 'th'
      ? translations[key].th
      : translations[key].en;
    return translation || fallback || key;
  }, [currentLanguage, translations]);

  const value = {
    currentLanguage,
    setCurrentLanguage,
    translations,
    print_lang,
    loadBranchLanguage,
    updateBranchLanguage,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
